// Painel de trocas de dispositivo: junta numa linha por paciente + dispositivo tudo o que o
// Quality e o IW sabem sobre a troca.
//  - IW (FTP): quem está em atendimento e quais dispositivos usa (TDEXP_ATEND), as evoluções
//    "SOL/EVOL TROCA …" (TDEXP_DISPOSITIVOS) e os aditivos de material (TDEXP_ADITIVOS);
//  - Quality: trocas_dispositivos (planilha antiga + FTP), pedidos de troca hospitalar
//    (dispositivos_solicitacoes) e o controle de cânulas/sondas em casa (rm_canulas).
// Resultado: última troca, próxima, situação do prazo, onde troca (casa/hospital) e em que
// etapa está a próxima (pedido, aditivo, autorização, agendamento).

export const FAMILIAS = {
  gtt: { nome: 'Gastrostomia (botton / Kangaroo / PE)', curto: 'Gastrostomia', periodo: 180 },
  tqt: { nome: 'Traqueostomia (cânula)', curto: 'Traqueostomia', periodo: 90 },
  cvd: { nome: 'Sonda vesical (Foley)', curto: 'Sonda vesical', periodo: 30 },
  cisto: { nome: 'Cistostomia', curto: 'Cistostomia', periodo: 30 },
  picc: { nome: 'PICC', curto: 'PICC', periodo: null },
  port: { nome: 'Port-a-cath (ativação)', curto: 'Port', periodo: 60 },
  sne: { nome: 'Sonda nasoenteral', curto: 'SNE', periodo: null },
  ext: { nome: 'Extensor do botton', curto: 'Extensor', periodo: null },
};

export function familiaDe(texto) {
  const d = norm(texto);
  if (/EXTENSOR/.test(d)) return 'ext';
  if (/CISTOST/.test(d)) return 'cisto';
  if (/BOTTON|KANGAROO|SONDA PE\b|PEG|GASTRO|GTT|JEJUNO/.test(d)) return 'gtt';
  if (/TRAQUE|TQT|CANULA/.test(d)) return 'tqt';
  if (/FOLEY|VESICAL|SVD/.test(d)) return 'cvd';
  if (/PICC/.test(d)) return 'picc';
  if (/\bSNE\b|\bSNG\b|NASOENTERAL|NASOGASTR/.test(d)) return 'sne';
  return null;
}

// O IW usa sonda Foley também na gastrostomia e na cistostomia, e registra a ativação do PORT no
// template de PICC: o cadastro do paciente no IW (Sim/Não por dispositivo) decide (achado de 16/10/2026).
const CAMPO_IW = { gtt: 'gtt', tqt: 'tqt', cvd: 'cvd', cisto: 'cist', picc: 'picc', port: 'port', sne: 'sne' };
export function familiaDoPaciente(fam, pac) {
  const d = pac?.dispositivos;
  if (!fam || !d) return fam;
  const usa = c => d[c]?.valor === 'sim';
  if (fam === 'cvd' && !usa('cvd')) return usa('cist') ? 'cisto' : usa('gtt') ? 'gtt' : fam;
  if (fam === 'picc' && usa('port') && !usa('picc')) return 'port';
  return fam;
}

export function norm(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}
const PEQ = new Set(['DE', 'DA', 'DO', 'DAS', 'DOS', 'E']);
const tokens = s => norm(s).replace(/[0-9]/g, ' ').split(' ').filter(t => t && !PEQ.has(t));
function mesmoNome(a, b) {
  const x = tokens(a), y = tokens(b);
  if (x.length < 2 || y.length < 2 || x[0] !== y[0] || x[x.length - 1] !== y[y.length - 1]) return false;
  const sy = new Set(y);
  return x.filter(w => sy.has(w)).length >= Math.min(3, x.length, y.length);
}

const DIA = 864e5;
const hoje = () => { const h = new Date(); h.setHours(0, 0, 0, 0); return h; };
// aceita "aaaa-mm-dd", "dd/mm/aaaa" e ISO
export function data(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  const s = String(v);
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return null;
}
const maisDias = (d, n) => new Date(d.getTime() + n * DIA);

export function situacaoDoPrazo(dias) {
  if (dias === null) return { id: 'semData', rotulo: 'Sem data', cor: '#475569', fundo: '#f1f5f9' };
  if (dias < 0) return { id: 'atrasada', rotulo: `Atrasada ${-dias} dia${dias === -1 ? '' : 's'}`, cor: '#991b1b', fundo: '#fee2e2' };
  if (dias <= 7) return { id: 'ate7', rotulo: dias === 0 ? 'Vence hoje' : `Vence em ${dias} dia${dias === 1 ? '' : 's'}`, cor: '#9a3412', fundo: '#ffedd5' };
  if (dias <= 30) return { id: 'ate30', rotulo: `Vence em ${dias} dias`, cor: '#854d0e', fundo: '#fef9c3' };
  return { id: 'emDia', rotulo: `Em dia (${dias} dias)`, cor: '#166534', fundo: '#dcfce7' };
}

const FINALIZADA = /realizad|cancelad|conclu|negad/i;

// Aditivo de material que corresponde a uma família de dispositivo
function familiaDoAditivo(item) {
  const d = norm(item);
  if (/EXTENSOR/.test(d)) return 'ext';
  if (/SONDA GASTRO|KANGAROO|BOTTON|SONDA PE\b|PEG\b|JEJUNO/.test(d)) return 'gtt';
  if (/CANULA|TQT/.test(d) && !/ASPIRACAO/.test(d)) return 'tqt';
  if (/FOLEY/.test(d)) return 'cvd';
  if (/PICC/.test(d)) return 'picc';
  return null;
}

/**
 * Monta as linhas do painel.
 * @param iw { pacientes, historico, evolucoesTroca }  (/api/iw-pacientes)
 * @param trocas trocas_dispositivos · solicitacoes dispositivos_solicitacoes · canulas rm_canulas
 * @param aditivos itens de aditivo (/api/iw-pedidos, sem valores)
 */
export function montarPainel({ iw, trocas = [], solicitacoes = [], canulas = [], aditivos = [] }) {
  const ativos = iw?.pacientes || [];
  const porNum = new Map(ativos.map(p => [String(p.numIW), p]));
  const cacheNome = new Map();
  // Paciente em atendimento no IW: pelo nº do atendimento ou pelo nome
  const pacienteDe = (nome, numIW) => {
    if (numIW && porNum.has(String(numIW))) return porNum.get(String(numIW));
    const k = norm(nome);
    if (!cacheNome.has(k)) cacheNome.set(k, ativos.find(p => norm(p.nome) === k) || ativos.find(p => mesmoNome(nome, p.nome)) || null);
    return cacheNome.get(k);
  };

  const linhas = new Map(); // chave → linha
  const linhaDe = (nome, numIW, fam, nomeDisp) => {
    const pac = pacienteDe(nome, numIW);
    const chave = `${pac ? `iw${pac.numIW}` : `nome:${norm(nome)}`}|${fam}`;
    if (!linhas.has(chave)) {
      linhas.set(chave, {
        chave, familia: fam, dispositivo: nomeDisp || FAMILIAS[fam]?.curto || fam,
        nome: pac?.nome || nome, numIW: pac?.numIW || numIW || '', unidade: pac?.unidade || '—', paciente: pac,
        emAtendimento: !!pac, trocasFeitas: [], pedidosIW: [], pedidosHospital: [], canula: null, aditivos: [],
        operadora: '', liminar: false, localManual: '', idRegistro: null, locais: [],
      });
    }
    const l = linhas.get(chave);
    if (nomeDisp) l.dispositivoNomes = new Set([...(l.dispositivoNomes || []), nomeDisp]);
    return l;
  };

  // 1. Trocas registradas (planilha antiga + FTP do IW) e evoluções frescas do IW
  const vistosEvol = new Set();
  const registrarTroca = (t, origem) => {
    const fam = familiaDoPaciente(familiaDe(t.dispositivo), pacienteDe(t.paciente, t.numIW));
    if (!fam) return;
    const l = linhaDe(t.paciente, t.numIW, fam, t.dispositivo);
    const reg = {
      ultima: data(t.ultima), proxima: data(t.proxima), periodo: Number(t.periodo) || null, quando: data(t.quando) || data(t.ultima),
      origem, solicitacao: !!t.solicitacao, local: t.local || '', id: t.id || null, dispositivo: t.dispositivo,
    };
    if (reg.solicitacao) l.pedidosIW.push(reg);
    if (reg.ultima) l.trocasFeitas.push(reg);
    if (reg.local) l.locais.push({ local: reg.local, quando: reg.quando, fonte: 'IW' });
    if (t.operadora) l.operadora = t.operadora;
    if (t.liminar) l.liminar = true;
    if (t.localManual && (!l._localManualEm || (reg.quando && reg.quando > l._localManualEm))) { l.localManual = t.localManual; l._localManualEm = reg.quando; l.idRegistro = t.id; }
    if (t.id && !l.idRegistro) l.idRegistro = t.id;
  };
  for (const e of iw?.evolucoesTroca || []) {
    vistosEvol.add(String(e.evolucao));
    registrarTroca({ paciente: e.paciente, numIW: e.numIW, dispositivo: e.dispositivo, ultima: e.ultima, proxima: e.proxima, periodo: e.periodo, quando: e.dataEvolucao, solicitacao: e.solicitacao, local: e.local, operadora: e.operadora, liminar: e.liminar }, 'IW');
  }
  for (const t of trocas) {
    if (t.iw_evolucao && vistosEvol.has(String(t.iw_evolucao))) {
      // já veio fresco do IW; só aproveita a escolha manual de casa/hospital e o id do registro
      const fam = familiaDoPaciente(familiaDe(t.dispositivo), pacienteDe(t.paciente_nome, t.num_iw));
      if (fam && t.local_proxima_troca) { const l = linhaDe(t.paciente_nome, t.num_iw, fam, t.dispositivo); l.localManual = t.local_proxima_troca; l.idRegistro = t.id; }
      else if (fam) { const l = linhaDe(t.paciente_nome, t.num_iw, fam, t.dispositivo); if (!l.idRegistro) l.idRegistro = t.id; }
      continue;
    }
    registrarTroca({
      paciente: t.paciente_nome, numIW: t.num_iw, dispositivo: t.dispositivo, ultima: t.data_ultima_troca, proxima: t.data_proxima_troca,
      periodo: t.periodo_dias, quando: t.data_evolucao || t.data_ultima_troca, solicitacao: /\bSOL\b/i.test(t.tipo_template || '') || t.solicitacao,
      operadora: t.operadora, liminar: t.tem_liminar === 'Sim', id: t.id, localManual: t.local_proxima_troca,
    }, t.origem === 'IW-FTP' ? 'IW' : 'planilha');
  }

  // 1b. Remoções cobradas pela SOS para troca de cânula/gastrostomia: a troca aconteceu no hospital
  for (const r of iw?.trocasPorRemocao || []) {
    const fam = familiaDe(r.dispositivo);
    if (!fam || !data(r.data)) continue;
    const l = linhaDe(r.paciente, r.numIW, fam, null);
    l.trocasFeitas.push({ ultima: data(r.data), proxima: null, periodo: null, quando: data(r.data), origem: 'remoção cobrada (SOS)', local: 'Hospitalar', destino: r.destino });
    l.locais.push({ local: 'Hospitalar', quando: data(r.data), fonte: 'remoção' });
  }

  // 2. Cânulas/sondas acompanhadas em casa (rm_canulas — Unimed Brasil, Unimed Outros, Bradesco, Sondas HB)
  for (const c of canulas) {
    const fam = c.tipo === 'sondas' ? 'gtt' : 'tqt';
    const l = linhaDe(c.paciente, null, fam, c.tipo === 'sondas' ? 'SONDA' : 'TRAQUEOSTOMIA');
    l.canula = c;
    const u = data(c.ultima_troca);
    if (u) l.trocasFeitas.push({ ultima: u, proxima: data(c.proxima_troca), periodo: Number(String(c.periodicidade || '').replace(/\D/g, '')) || null, quando: u, origem: 'cânulas' });
    if (c.local) l.locais.push({ local: /hosp/i.test(c.local) ? 'Hospitalar' : 'Domiciliar', quando: u, fonte: 'Quality' });
  }

  // 3. Pedidos de troca hospitalar (dispositivos_solicitacoes)
  for (const s of solicitacoes) {
    const fam = familiaDe(s.dispositivo) || 'tqt';
    const l = linhaDe(s.paciente, null, fam, null);
    const quando = data(s.data) || data(s.criadoEm);
    l.pedidosHospital.push({ status: s.status || 'Em análise', data: data(s.data), criado: data(s.criadoEm), local: s.local || '', aditivo: s.aditivo_iw || '', quando });
    if (FINALIZADA.test(s.status || '') && /realizad/i.test(s.status || '') && data(s.data)) {
      l.trocasFeitas.push({ ultima: data(s.data), proxima: null, periodo: null, quando: data(s.data), origem: 'troca hospitalar' });
    }
  }

  // 4. Aditivos de material do IW (só quem tem nº do atendimento)
  for (const a of aditivos) {
    const fam = familiaDoAditivo(a.item);
    if (!fam || !a.numIW) continue;
    const pac = porNum.get(String(a.numIW));
    const chave = `${pac ? `iw${pac.numIW}` : `nome:${norm(a.paciente)}`}|${fam}`;
    const l = linhas.get(chave);
    if (l) l.aditivos.push({ ...a, quando: data(a.criadoEm) });
  }

  // 5. Quem o IW diz usar o dispositivo (cadastro do atendimento) e não tem nenhum registro
  for (const p of ativos) {
    for (const [campo, fam] of [['gtt', 'gtt'], ['tqt', 'tqt'], ['cvd', 'cvd'], ['cist', 'cisto'], ['picc', 'picc'], ['port', 'port']]) {
      if (p.dispositivos?.[campo]?.valor === 'sim' && !linhas.has(`iw${p.numIW}|${fam}`)) linhaDe(p.nome, p.numIW, fam, null);
    }
  }

  // Consolidação de cada linha
  const h = hoje();
  const resultado = [];
  for (const l of linhas.values()) {
    const feitas = l.trocasFeitas.filter(t => t.ultima).sort((a, b) => b.ultima - a.ultima || (b.quando || 0) - (a.quando || 0));
    const ult = feitas[0] || null;
    const ultima = ult?.ultima || null;
    const periodo = ult?.periodo || FAMILIAS[l.familia]?.periodo || null;
    let proxima = ult?.proxima && ultima && ult.proxima > ultima ? ult.proxima : null;
    let proximaCalculada = false;
    if (!proxima && ultima && periodo) { proxima = maisDias(ultima, periodo); proximaCalculada = true; }
    const dias = proxima ? Math.round((proxima - h) / DIA) : null;
    const depoisDaUltima = d => !ultima || (d && d > ultima);

    // Etapa da próxima troca
    const pedidoIW = l.pedidosIW.filter(p => depoisDaUltima(p.quando)).sort((a, b) => b.quando - a.quando)[0] || null;
    const pedidoHosp = l.pedidosHospital.filter(p => !FINALIZADA.test(p.status) && depoisDaUltima(p.criado || p.quando)).sort((a, b) => (b.criado || 0) - (a.criado || 0))[0] || null;
    const aditivo = l.aditivos.filter(a => depoisDaUltima(a.quando)).sort((a, b) => (b.quando || 0) - (a.quando || 0))[0] || null;
    const agendadaCasa = l.canula && data(l.canula.proxima_troca) && depoisDaUltima(data(l.canula.proxima_troca)) ? data(l.canula.proxima_troca) : null;

    // Onde troca: escolha manual > pedido hospitalar aberto > o que o IW/Quality registrou por último
    const ultimoLocal = l.locais.filter(x => x.local).sort((a, b) => (b.quando || 0) - (a.quando || 0))[0];
    const local = l.localManual || (pedidoHosp ? 'Hospitalar' : '') || ultimoLocal?.local || '';
    const localOrigem = l.localManual ? 'definido aqui' : pedidoHosp ? 'pedido hospitalar' : ultimoLocal ? `pelo ${ultimoLocal.fonte}` : '';

    const naoUsaMais = !!l.paciente && !!CAMPO_IW[l.familia] && l.paciente.dispositivos?.[CAMPO_IW[l.familia]]?.valor === 'nao';
    const soNoIW = !!pedidoIW && !pedidoHosp && !(l.canula && agendadaCasa);
    const etapa = pedidoHosp ? `Troca hospitalar: ${pedidoHosp.status}` : aditivo ? 'Aditivo lançado' : pedidoIW ? 'Pedido feito no IW' : 'Sem pedido';

    resultado.push({
      ...l,
      dispositivo: [...(l.dispositivoNomes || [])].filter(n => n && n !== 'N/I').pop() || FAMILIAS[l.familia]?.curto || l.familia,
      ultima, proxima, proximaCalculada, periodo, dias, situacao: situacaoDoPrazo(dias),
      origemUltima: ult?.origem || '',
      pedidoIW, pedidoHosp, aditivo, agendadaCasa,
      etapa, soNoIW, naoUsaMais,
      local, localOrigem,
    });
  }
  return resultado;
}
